import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToggle } from 'react-use';
import { IconButton } from 'src/components/Buttons';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content, useContentContext } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { HamburgerMenuIcon } from 'src/svgs';
import { EditIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  Listing,
  SectionInfo,
  SectionSectionGroup,
} from 'src/WebApiController';

import { ClickToExpand } from './ClickToExpand';
import * as styles from './DragableSectionGroupItem.css';
import { ModifySectionGroup } from './ModifySectionGroup';
import { toDisplayName, toListingStats } from './SectionGroupPanelV2.utils';

export const DragableSectionGroupItem = ({
  id,
  eventId,
  sectionGroup,
  existingGroupIds,
  sections,
  onUpdateSectionGroup,
  onRemoveSectionGroup,
}: {
  id: string;
  eventId: string;
  sectionGroup: SectionSectionGroup;
  existingGroupIds: string[];
  sections: SectionInfo[];
  onUpdateSectionGroup: (
    key: string,
    sectionGroup: SectionSectionGroup
  ) => void;
  onRemoveSectionGroup: (key: string) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: id });
  const contentContext = useContentContext();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Stack
      width="full"
      alignItems="center"
      justifyContent="spaceBetween"
      ref={setNodeRef}
      style={style}
      {...attributes}
      key={id}
      className={styles.sortableZoneAreaItemContainer}
    >
      <Stack>
        <ReactiveSectionGroupColor
          id={id}
          sectionGroup={sectionGroup}
          sections={sections}
          existingGroupIds={existingGroupIds}
          onUpdateSectionGroup={onUpdateSectionGroup}
          onRemoveSectionGroup={onRemoveSectionGroup}
        />
        <span>{toDisplayName(sectionGroup, contentContext)}</span>
      </Stack>
      <SectionGroupItemMetrics
        eventId={eventId}
        sections={sections}
        listeners={listeners}
      />
    </Stack>
  );
};

/**
 * Separate metrics as a stand alone metrics to access the catalog data
 * To avoid rerender of the section group color modal
 * https://thestubhub.atlassian.net/browse/SGR-2
 */
const SectionGroupItemMetrics = ({
  eventId,
  sections,
  listeners,
}: {
  eventId: string;
  sections: SectionInfo[];
  listeners: SyntheticListenerMap | undefined;
}) => {
  const { eventsTransformed } = useCatalogDataContext();

  const sectionIds = useMemo(() => sections.map(({ id }) => id), [sections]);

  const groupListings = useMemo(() => {
    const listings = (eventsTransformed ?? [])
      .filter((event) => event.event.virtId === eventId)
      .flatMap((event) => event.entities.listings ?? [])
      .flatMap(flattenListingGroup)
      .map((l) => l as Listing)
      .filter((l) => !l.isLtGrp && l.id > 0 && Boolean(l.seating?.sectionId));
    return listings.filter(
      (l) => l.seating?.sectionId && sectionIds.includes(l.seating?.sectionId)
    );
  }, [eventId, eventsTransformed, sectionIds]);

  const listingStats = useMemo(
    () => toListingStats(groupListings),
    [groupListings]
  );

  return (
    <Stack alignItems="center" gap="m">
      <div className={styles.sectionGroupStats}>
        <span>
          {sectionIds.length.toLocaleString()}{' '}
          <Content id={ContentId.Sections} />
        </span>
        {', '}
        <span>
          {listingStats.listingCount} <Content id={ContentId.Listings} />
        </span>
      </div>
      <div {...listeners}>
        <HamburgerMenuIcon
          size={vars.iconSize.m}
          fill={vars.color.textDisabled}
        />
      </div>
    </Stack>
  );
};

const ReactiveSectionGroupColor = ({
  id,
  sectionGroup,
  sections,
  existingGroupIds,
  onUpdateSectionGroup,
  onRemoveSectionGroup,
}: {
  id: string;
  sectionGroup: SectionSectionGroup;
  existingGroupIds: string[];
  sections: SectionInfo[];
  onUpdateSectionGroup: (
    key: string,
    sectionGroup: SectionSectionGroup
  ) => void;
  onRemoveSectionGroup: (key: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const { listeners, hovering } = useHovering(open);

  const onSaveSectionGroup = useCallback(
    (updated: SectionSectionGroup) => {
      setOpen(false);
      onUpdateSectionGroup(id, updated);
    },
    [id, onUpdateSectionGroup]
  );
  const onDeleteSectionGroup = useCallback(() => {
    setOpen(false);
    onRemoveSectionGroup(id);
  }, [id, onRemoveSectionGroup]);

  return (
    <ClickToExpand
      id={id}
      open={open}
      setOpen={setOpen}
      trigger={
        <div
          {...listeners}
          className={styles.zoneGroupColorBox}
          style={{
            backgroundColor: hovering
              ? 'unset'
              : sectionGroup.sectionGroupColor,
          }}
        >
          <IconButton
            style={{
              padding: 'unset',
              visibility: hovering ? 'visible' : 'hidden',
              cursor: 'pointer',
            }}
            icon={<EditIcon size={vars.iconSize.m} />}
          />
        </div>
      }
      titleContentId={ContentId.EditZone}
      content={
        <ModifySectionGroup
          sectionGroup={sectionGroup}
          selectedSections={sections}
          existingGroupIds={existingGroupIds}
          onSaveSectionGroup={onSaveSectionGroup}
          onDeleteSectionGroup={onDeleteSectionGroup}
        />
      }
    />
  );
};

// Light weight hook to check if hovering on the component
const useHovering = (disabled?: boolean) => {
  const [hovering, setHovering] = useToggle(false);

  // Reset hovering state when disabled changes (e.g., modal opens)
  useEffect(() => {
    setHovering(false);
  }, [disabled, setHovering]);

  const onMouseEnter = useCallback(() => {
    if (!disabled) {
      setHovering(true);
    }
  }, [disabled, setHovering]);

  const onMouseLeave = useCallback(() => {
    setHovering(false);
  }, [setHovering]);

  // Memoize listeners object to maintain referential equality
  const listeners = useMemo(
    () => ({
      onMouseEnter,
      onMouseLeave,
    }),
    [onMouseEnter, onMouseLeave]
  );

  return {
    hovering,
    listeners,
  };
};
